import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import {
  ThemeAICodeFrame,
  ThemeAICodeFrameListResponse,
  ThemeAICodeFrameVersion,
  ThemeAITheme,
  ThemeAIGroup,
  ThemeAITask,
  CreateThemeAICodeFrameRequest,
  UpdateThemeAICodeFrameRequest,
  ThemeAIStandardFrames,
} from '../types/gotypes'

interface UseThemeAICodeFramesOptions {
  customer: string
  job: string
  includeArchived?: boolean
  frameId?: string
  /** When set, handleImportCodeFrame also selects the imported frame on this task. */
  taskId?: string
}

// Code frame library CRUD + versioning (design spec #916 §4). Versions are
// immutable — editing themes always mints a new version via
// createCodeFrameVersion (never PUTs themes onto the frame itself).
export const useThemeAICodeFrames = ({ customer, job, includeArchived, frameId, taskId }: UseThemeAICodeFramesOptions) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const baseUrl = `${API_BASE_URL}/themeai/${customer}/${job}`
  const listQueryKey = ['themeai_code_frames', { customer, job, includeArchived }]
  const frameQueryKey = ['themeai_code_frame', { customer, job, frameId }]

  const framesQuery = useQuery({
    queryKey: listQueryKey,
    enabled: Boolean(customer && job && account.user),
    queryFn: async () => {
      const res = await axios.get<ThemeAICodeFrameListResponse>(`${baseUrl}/code-frames`, {
        params: { include_archived: includeArchived ? 'true' : undefined },
      })
      return res.data
    },
  })

  // The business-wide sentiment and emotion frames (spec #916 §9.2): shown,
  // not edited, when a run switches them on.
  const standardFramesQuery = useQuery({
    queryKey: ['themeai_standard_frames', { customer, job }],
    enabled: Boolean(customer && job && account.user),
    staleTime: Infinity,
    queryFn: async () => {
      const res = await axios.get<ThemeAIStandardFrames>(`${baseUrl}/standard-frames`)
      return res.data
    },
  })

  const frameQuery = useQuery({
    queryKey: frameQueryKey,
    enabled: Boolean(customer && job && frameId && account.user),
    queryFn: async () => {
      const res = await axios.get<ThemeAICodeFrame>(`${baseUrl}/code-frames/${frameId}`)
      return res.data
    },
  })

  // Returns the invalidation promise so every mutation below can `return` it
  // from onSuccess — TanStack v5 awaits whatever onSuccess returns before
  // flipping isPending back to false, so an awaited mutateAsync() sees
  // post-invalidation state instead of a flash of stale cached data (this is
  // what caused the "stage not available yet" flash after saves).
  const invalidateAll = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['themeai_code_frames', { customer, job }], exact: false }),
    ...(frameId ? [queryClient.invalidateQueries({ queryKey: frameQueryKey })] : []),
  ])

  const createCodeFrameMutation = useMutation({
    mutationFn: async (payload: CreateThemeAICodeFrameRequest) => {
      const res = await axios.post<ThemeAICodeFrame>(`${baseUrl}/code-frames`, payload)
      return res.data
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  const updateCodeFrameMutation = useMutation({
    mutationFn: async (payload: { id: string } & UpdateThemeAICodeFrameRequest) => {
      const { id, ...data } = payload
      const res = await axios.put<ThemeAICodeFrame>(`${baseUrl}/code-frames/${id}`, data)
      return res.data
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  const createVersionMutation = useMutation({
    mutationFn: async (payload: { frameId: string; themes: ThemeAITheme[]; groups?: ThemeAIGroup[]; changeNote?: string }) => {
      const res = await axios.post<ThemeAICodeFrameVersion>(`${baseUrl}/code-frames/${payload.frameId}/versions`, {
        themes: payload.themes,
        groups: payload.groups,
        change_note: payload.changeNote,
      })
      return res.data
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  const importCodeFrameMutation = useMutation({
    mutationFn: async (payload: { file: File; name?: string; visibility?: 'exec' | 'user' }) => {
      const form = new FormData()
      form.append('file', payload.file)
      if (payload.name) form.append('name', payload.name)
      if (payload.visibility) form.append('visibility', payload.visibility)
      const res = await axios.post<ThemeAICodeFrame>(`${baseUrl}/code-frames/import`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  const createCodeFrame = (payload: CreateThemeAICodeFrameRequest) => createCodeFrameMutation.mutateAsync(payload)
  const updateCodeFrame = (id: string, payload: UpdateThemeAICodeFrameRequest) =>
    updateCodeFrameMutation.mutateAsync({ id, ...payload })
  const createCodeFrameVersion = (
    codeFrameId: string,
    themes: ThemeAITheme[],
    groups?: ThemeAIGroup[],
    changeNote?: string,
  ) => createVersionMutation.mutateAsync({ frameId: codeFrameId, themes, groups, changeNote })
  const importCodeFrame = (file: File, name?: string, visibility?: 'exec' | 'user') =>
    importCodeFrameMutation.mutateAsync({ file, name, visibility })

  // -- Handlers used by the Code Frame stage (Page -> Hook -> Component) --

  // Selects a code frame version on the task (PUT /tasks/:taskId) — belongs
  // here rather than in useThemeAITasks because it's only ever the second
  // half of an operation this hook starts (import, and any future "pick an
  // existing frame" flow). Requires the hook to have been constructed with
  // taskId.
  const selectCodeFrameOnTaskMutation = useMutation({
    mutationFn: async (payload: { codeFrameId: string; codeFrameVersion: number }) => {
      if (!taskId) throw new Error('no task to select this code frame on')
      const res = await axios.put<ThemeAITask>(`${baseUrl}/tasks/${taskId}`, {
        code_frame_id: payload.codeFrameId,
        code_frame_version: payload.codeFrameVersion,
      })
      return res.data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['themeai_task', { customer, job, taskId }], exact: false }),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  // Imports a frame from CSV/Excel via the server importer, then (when the
  // hook was constructed with a taskId) selects it on the task so
  // task.code_frame_id/version point at it.
  const handleImportCodeFrame = async (file: File, name?: string, visibility?: 'exec' | 'user') => {
    const frame = await importCodeFrameMutation.mutateAsync({ file, name, visibility })
    if (taskId) {
      await selectCodeFrameOnTaskMutation.mutateAsync({ codeFrameId: frame.id, codeFrameVersion: frame.latest_version })
    }
    return frame
  }

  // A Manager moves a frame to or from the company level, as a Data Viz item
  // is starred (Matt, 23 Sep: "exactly the same style as Data Viz"). The
  // server allows it for Managers only.
  const handlePromoteCodeFrame = (id: string) => updateCodeFrameMutation.mutate({ id, visibility: 'exec' })
  const handleDemoteCodeFrame = (id: string) => updateCodeFrameMutation.mutate({ id, visibility: 'user' })

  const saveCopyMutation = useMutation({
    mutationFn: async (payload: { id: string; name: string }) => {
      const res = await axios.post<ThemeAICodeFrame>(`${baseUrl}/code-frames/${payload.id}/save-as`, { name: payload.name })
      return res.data
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  // Copies a frame into the user's own frames and, when the hook has a task,
  // selects the copy on it. A standard frame is read-only, so this is how
  // someone edits one.
  const handleSaveCopyOfFrame = async (id: string, name: string) => {
    const frame = await saveCopyMutation.mutateAsync({ id, name })
    if (taskId) {
      await selectCodeFrameOnTaskMutation.mutateAsync({ codeFrameId: frame.id, codeFrameVersion: frame.latest_version || 1 })
    }
    return frame
  }

  // Saves an edited theme list: a new version when the frame already exists
  // (versions are immutable — edits always mint a new one), otherwise creates
  // the frame itself from the themes.
  const handleSaveThemes = (
    themes: ThemeAITheme[],
    options: { frameId?: string; name?: string; visibility?: ThemeAICodeFrame['visibility']; groups?: ThemeAIGroup[]; changeNote?: string },
  ) => {
    if (options.frameId) {
      return createVersionMutation.mutateAsync({
        frameId: options.frameId,
        themes,
        groups: options.groups,
        changeNote: options.changeNote,
      })
    }
    return createCodeFrameMutation.mutateAsync({
      name: options.name || 'Untitled code frame',
      visibility: options.visibility,
      themes,
      groups: options.groups,
    })
  }

  return {
    codeFrames: framesQuery.data || { exec: [], mine: [], colleagues: [] },
    standardFrames: standardFramesQuery.data,
    codeFramesLoading: framesQuery.isLoading,
    codeFramesError: framesQuery.error,

    codeFrame: frameQuery.data,
    codeFrameLoading: frameQuery.isLoading,
    codeFrameError: frameQuery.error,

    createCodeFrame,
    updateCodeFrame,
    createCodeFrameVersion,
    importCodeFrame,
    handleImportCodeFrame,
    handleSaveThemes,
    handlePromoteCodeFrame,
    handleDemoteCodeFrame,
    handleSaveCopyOfFrame,

    saveCopyLoading: saveCopyMutation.isPending || selectCodeFrameOnTaskMutation.isPending,
    createCodeFrameLoading: createCodeFrameMutation.isPending,
    createVersionLoading: createVersionMutation.isPending,
    importCodeFrameLoading: importCodeFrameMutation.isPending || selectCodeFrameOnTaskMutation.isPending,
    saveThemesLoading: createVersionMutation.isPending || createCodeFrameMutation.isPending,

    refetchCodeFrames: framesQuery.refetch,
    refetchCodeFrame: frameQuery.refetch,
  }
}

export type ThemeAICodeFramesController = ReturnType<typeof useThemeAICodeFrames>

export default useThemeAICodeFrames
